<script setup lang="ts">
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { NSpin } from "naive-ui";
import { computed, onMounted, reactive, ref } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import BackgoundImage from "../assets/images/login-background.jpeg";
import PasswordValidator from "../components/PasswordValidator.vue";
import { signInWithGoogle, signInWithPassword, signOut } from "../services/auth";
import {
    activateWithGoogle,
    checkLoginStatus,
    getEmailForToken,
    setPasswordForUser,
} from "../services/user";
import {
    isOnboardingEnabled,
    loadOnboardingProgressFromBackend,
} from "../constants/onboardingProgress";

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();

async function goToOnboarding() {
    await queryClient.fetchQuery({
        queryKey: ["onboarding-progress"],
        queryFn: loadOnboardingProgressFromBackend,
        staleTime: 0,
    });

    if (!isOnboardingEnabled()) {
        await router.push("/");
        return;
    }

    await router.push("/onboarding");
}

const isActivation = computed<boolean>(() => route.query.token !== undefined);
const token = computed<string>(() => (route.query.token as string) ?? "");

const isLoading = computed<boolean>(() => {
    return (
        activationPending.value ||
        googleActivationPending.value ||
        signInWithGooglePending.value ||
        loginPending.value ||
        state.loadingTokenValidate
    );
});

const state = reactive({
    username: "",
    password: "",
    email: "",
    error: "",
    usernameError: "",
    passwordError: "",
    loadingTokenValidate: false,
    rememberMe: false,
    showPassword: false,
});

const { mutate: onLogin, isPending: loginPending } = useMutation({
    mutationFn: async () => signInWithPassword(state.username, state.password),
    onError(e: Error) {
        console.log(e);
        // Map server/auth messages to field-specific errors when possible
        const msg = e.message || "Invalid username or password.";
        state.error = "";
        if (msg.includes("Too many failed login attempts")) {
            state.error = msg;
            return;
        }
        if (msg.includes("Wrong password")) {
            state.passwordError = msg;
        } else if (msg.includes("email does not exist") || msg.includes("Invalid email")) {
            state.usernameError = msg;
        } else if (msg.includes("Unauthorized")) {
            state.error = msg;
        } else {
            state.error = msg;
        }
    },
    onSuccess: goToOnboarding,
});

const activationPasswordValid = ref(false);
function onActivationPasswordValidChange(v: boolean) {
    activationPasswordValid.value = v;
}

const { mutate: activateAccount, isPending: activationPending } = useMutation({
    mutationFn: async function () {
        await setPasswordForUser({
            token: token.value,
            password: state.password,
        });
        await signInWithPassword(state.email, state.password);
    },
    onError(e: Error) {
        state.error = e.message;
    },
    onSuccess: goToOnboarding,
});

const { mutate: activateWithGoogleAccount, isPending: googleActivationPending } = useMutation({
    mutationFn: async function () {
        const [_, firebaseUser] = await signInWithGoogle();
        await activateWithGoogle(token.value, firebaseUser.email!);
    },
    onError: async (e: Error) => {
        state.error = e.message;
        await signOut();
    },
    onSuccess: goToOnboarding,
});

const { mutate: onSignInWithGoogle, isPending: signInWithGooglePending } = useMutation({
    mutationFn: async () => {
        const [token, firebaseUser] = await signInWithGoogle();

        // Check if user is allowed to login after Firebase auth
        const loginCheck = await checkLoginStatus(firebaseUser.email!);

        if (!loginCheck.allowed) {
            await signOut();
            throw new Error(loginCheck.message || "Login not allowed");
        }

        return [token, firebaseUser];
    },
    onSuccess: goToOnboarding,
    onError(e: Error) {
        state.error = e.message || "Sorry we couldn't log you in with Google :(";
    },
});

async function onSubmitEmailLogin() {
    // Reset field errors
    state.usernameError = "";
    state.passwordError = "";
    state.error = "";

    if (!state.username) {
        state.usernameError = "You must provide an email.";
    }
    if (!state.password) {
        state.passwordError = "Please enter a password.";
    }

    if (state.usernameError || state.passwordError) {
        return;
    }

    // Check if user is allowed to login before calling Firebase
    try {
        const loginCheck = await checkLoginStatus(state.username);
        if (!loginCheck.allowed) {
            state.error = loginCheck.message || "Login not allowed";
            return;
        }
    } catch (e) {
        console.error("Login check failed:", e);
        state.error = "Unable to verify account. Please try again.";
        return;
    }

    onLogin();
}

onMounted(async () => {
    if (isActivation.value) {
        try {
            state.loadingTokenValidate = true;
            state.email = await getEmailForToken(route.query.token as string);
        } catch (e) {
            state.error = (e as Error).message;
        } finally {
            state.loadingTokenValidate = false;
        }
    } else {
        if (typeof route.query.email === "string") {
            state.username = route.query.email as string;
        }
    }
});
</script>

<template>
    <div
        class="h-full bg-cover bg-center rounded-xl flex flex-center gap-4"
        :style="{ backgroundImage: `url(${BackgoundImage})` }"
    >
        <div class="w-[610px] p-6 bg-cream-dark flex flex-col gap-8 rounded-lg">
            <template v-if="isLoading">
                <n-spin class="my-20" size="large" />
            </template>
            <template v-else>
                <template v-if="isActivation">
                    <h1 class="h1">Activate</h1>
                    <div v-if="state.error" class="error text-center">
                        {{ state.error }}
                    </div>
                    <template v-else>
                        <div class="font-regular">
                            Get jammin' with Proper by setting a password or signing in with Google.
                        </div>
                        <div class="flex flex-row gap-2 justify-center">
                            <button
                                @click="activateWithGoogleAccount()"
                                :disabled="googleActivationPending"
                                class="rounded bg-white py-2 px-5 flex flex-row items-center gap-2 hover:bg-cream transition duration-350 justify-evenly"
                                :class="{
                                    'opacity-50 cursor-not-allowed': googleActivationPending,
                                }"
                            >
                                <img src="../assets/images/google.svg" alt="Google" />
                                {{
                                    googleActivationPending
                                        ? "ACTIVATING..."
                                        : "SIGN IN WITH GOOGLE"
                                }}
                            </button>
                        </div>
                        <div class="flex flex-row text-xs items-center">
                            <span class="flex-expand h-px bg-border-gray"></span>
                            <span class="flex-shrink-0 px-2">OR SET PASSWORD</span>
                            <span class="flex-expand h-px bg-border-gray"></span>
                        </div>
                        <div class="relative">
                            <input
                                type="email"
                                :value="state.email"
                                placeholder="EMAIL"
                                disabled
                                class="input w-full bg-gray-500 cursor-not-allowed opacity-55 pr-12"
                            />
                            <div
                                class="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2"
                            >
                                <div
                                    class="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                                >
                                    <svg
                                        class="w-4 h-4 text-green"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <password-validator
                            v-model="state.password"
                            @valid-change="onActivationPasswordValidChange"
                        />
                        <div class="flex flex-row justify-between items-center">
                            <button
                                type="submit"
                                :disabled="activationPending || !activationPasswordValid"
                                @click="activateAccount()"
                                class="button w-min transition duration-350 -mt-3"
                                :class="{
                                    'opacity-50 cursor-not-allowed': !activationPasswordValid,
                                }"
                            >
                                {{ activationPending ? "ACTIVATING..." : "SET PASSWORD" }}
                            </button>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <h1 class="h1 text-center mt-4">Welcome back!</h1>
                    <div class="text-center -mt-4 text-sm">
                        Need access?
                        <RouterLink
                            to="/request-access"
                            class="underline text-blurple-700 font-bold"
                            >Ask your Proper Team</RouterLink
                        >
                    </div>
                    <div class="flex flex-row gap-2 justify-center">
                        <button
                            @click="onSignInWithGoogle()"
                            class="rounded bg-white py-2 px-5 flex flex-row items-center gap-2 hover:bg-cream transition duration-350 justify-evenly"
                        >
                            <img src="../assets/images/google.svg" alt="Google" />
                            SIGN IN WITH GOOGLE
                        </button>
                    </div>
                    <div class="flex flex-row text-xs items-center">
                        <span class="flex-expand h-px bg-border-gray"></span>
                        <span class="flex-shrink-0 px-2">OR WITH EMAIL</span>
                        <span class="flex-expand h-px bg-border-gray"></span>
                    </div>
                    <form @submit.prevent="onSubmitEmailLogin()" class="flex flex-col gap-4">
                        <input
                            type="text"
                            v-model="state.username"
                            placeholder="email"
                            name="username"
                            autocomplete="username"
                            class="input w-full text-sm"
                            :class="{
                                'border-red ring-1 ring-red-500 focus:ring-red ':
                                    state.usernameError,
                            }"
                            @input="state.usernameError = ''"
                        />
                        <div
                            v-if="state.usernameError"
                            class="text-red text-md font-bold -mt-2 ml-1"
                        >
                            {{ state.usernameError }}
                        </div>
                        <div class="relative">
                            <input
                                :type="state.showPassword ? 'text' : 'password'"
                                v-model="state.password"
                                placeholder="password"
                                name="password"
                                autocomplete="current-password"
                                class="input w-full pr-12 text-sm"
                                :class="{
                                    'border-red focus:ring-red': state.passwordError,
                                }"
                                @input="state.passwordError = ''"
                            />
                            <button
                                type="button"
                                @click="state.showPassword = !state.showPassword"
                                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-gray-400 transition-colors duration-200 flex items-center justify-center h-5 w-5 leading-none"
                            >
                                <span
                                    v-if="state.showPassword"
                                    class="material-symbols-outlined text-[20px]"
                                    >visibility_off</span
                                >
                                <span v-else class="material-symbols-outlined text-[20px]"
                                    >visibility</span
                                >
                            </button>
                        </div>
                        <div
                            v-if="state.passwordError"
                            class="text-red text-md font-bold -mt-2 ml-1"
                        >
                            {{ state.passwordError }}
                        </div>
                        <div class="flex flex-row justify-between items-center">
                            <div class="flex flex-row items-center gap-2 ml-[5px]">
                                <label
                                    class="flex flex-row items-center gap-2 cursor-pointer"
                                    @click="state.rememberMe = !state.rememberMe"
                                >
                                    <div
                                        class="w-4 h-4 rounded-full border-2 border-gray-400 flex items-center justify-center transition-colors duration-200"
                                        :class="{
                                            'bg-dark': state.rememberMe,
                                            'bg-transparent': !state.rememberMe,
                                        }"
                                    >
                                        <div
                                            v-if="state.rememberMe"
                                            class="w-2 h-2 rounded-full bg-dark"
                                        ></div>
                                    </div>
                                    <span class="text-xs font-mono text-gray-600 tracking-wider"
                                        >REMEMBER ME</span
                                    >
                                </label>
                            </div>
                            <router-link to="/forgot-password" class="text-blurple-100 text-xs"
                                >FORGOT PASSWORD?</router-link
                            >
                        </div>

                        <div v-if="state.error" class="error text-center">
                            {{ state.error }}
                        </div>

                        <button
                            type="submit"
                            class="button !w-full self-stretch block transition duration-350 justify-center mt-2"
                        >
                            SIGN IN
                        </button>
                    </form>
                </template>
            </template>
        </div>
    </div>
</template>
